import React, { useEffect, useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { faClipboard, faClose, faTable, faTableCells } from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { useError } from "../../../context/useErrorContext"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { Tables } from "../../../Objects/Table/Tables"
import { useGetTables } from "../../../hooks/Tables/useGetTables"
import { useSaveTable } from "../../../hooks/Tables/useSaveTable"
import { useCreateTable } from "../../../hooks/Tables/useCreateTable"
import InputField from "../../../components/InputField/InputField"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { tableMemory } from "../../../Objects/Memory/Memory"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useRemoveTable } from "../../../hooks/Tables/useRemoveTable"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { useTranslation } from "react-i18next"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { TableOrdersPopup } from "./TableOrdersPopup"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { Check, Clock3, Copy, Link2, LoaderCircle, QrCode, ShieldCheck, Tablet, Unlink } from "lucide-react"
import { useTableSelfService } from "../../../hooks/Tables/useTableSelfService"

interface TablesPupopProps {
  tableData?: Tables
  title: string
  onClonse: () => void
}

export const TablesPupop: React.FC<TablesPupopProps> = ({ tableData, title, onClonse }) => {
  const { t } = useTranslation("tables")

  const canEdite = !usePermission(Permissions.CanEditeTables)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { setError } = useError()

  const [showTableOrdersPopup, setShowTableOrdersPopup] = useState(false)

  const [activationCode, setActivationCode] = useState("")

  const [isTabletLinked, setIsTabletLinked] = useState(tableData?.isSelfServiceTabletLinked ?? false)

  const [hasPendingTabletActivation, setHasPendingTabletActivation] = useState(
    tableData?.hasActiveSelfServiceActivationCode ?? false,
  )

  const [tabletLastSeenAtUtc, setTabletLastSeenAtUtc] = useState(tableData?.selfServiceLastSeenAtUtc ?? "")

  const [tabletAction, setTabletAction] = useState<"generate" | "revoke" | null>(null)

  const [showTabletQrCode, setShowTabletQrCode] = useState(false)

  const [tabletLinkCopied, setTabletLinkCopied] = useState(false)

  const { handleSave } = useSaveTable()

  const { handleGetTable } = useGetTables()

  const { handleRemove } = useRemoveTable()

  const { handleCreateActivationCode, handleRevokeTablet } = useTableSelfService()

  const { setNewConfirmation } = useConfirmation()

  const restaurantSlug = companyMemory.slug?.trim() ?? ""

  const tabletUrl = restaurantSlug ? `${API_ROUTES.SITE}${restaurantSlug}/tablet` : ""

  const tabletActivationUrl = tabletUrl && activationCode ? `${tabletUrl}?activationCode=${activationCode}` : ""

  const tabletQrCodeUrl = tabletActivationUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=480x480&ecc=H&data=${encodeURIComponent(tabletActivationUrl)}`
    : ""

  const {
    Number: numberTable,
    Description: description,
    Status: status,
    handleSubmitForm,
    handleNumberTableChange,
    handleDescriptionChange,
    handleStatusChange,
    handleInitialValue,
  } = useCreateTable()

  useEffect(() => {
    const fakeEvent = {
      target: { value: (tableMemory.listData.length + 1).toString() },
    } as React.ChangeEvent<HTMLInputElement>
    handleNumberTableChange(fakeEvent)
    handleInitialValue(tableData)
    setIsTabletLinked(tableData?.isSelfServiceTabletLinked ?? false)
    setHasPendingTabletActivation(tableData?.hasActiveSelfServiceActivationCode ?? false)
    setTabletLastSeenAtUtc(tableData?.selfServiceLastSeenAtUtc ?? "")
  }, [tableData])

  const handleTrySubmit = (event: React.FormEvent) => {
    event.preventDefault()

    const result = handleSubmitForm()

    if ("errors" in result) {
      const errorMessages = result.errors.map((e) => e.message).join("\n")
      setError(t("general:label_invalid"), errorMessages)

      return
    }

    handleSave(result, () => {
      onClonse()

      handleGetTable(() => {})
    })
  }

  const createTabletActivationCode = async () => {
    if (!tableData || canEdite || isTabletLinked || tabletAction) return

    setTabletAction("generate")

    await handleCreateActivationCode(tableData.id, (activation) => {
      setActivationCode(activation.activationCode)
      setHasPendingTabletActivation(true)
      setShowTabletQrCode(false)
    })

    setTabletAction(null)
  }

  const revokeTablet = async () => {
    if (!tableData || canEdite || !isTabletLinked || tabletAction) return

    setTabletAction("revoke")

    await handleRevokeTablet(tableData.id, () => {
      setActivationCode("")
      setIsTabletLinked(false)
      setHasPendingTabletActivation(false)
      setTabletLastSeenAtUtc("")
      setShowTabletQrCode(false)
    })

    setTabletAction(null)
  }

  const confirmRevokeTablet = () => {
    if (!tableData || canEdite || !isTabletLinked) return

    setNewConfirmation(
      t("tableSelfService:unlink_confirmation_title"),
      t("tableSelfService:unlink_confirmation_description", { number: tableData.number }),
      revokeTablet,
    )
  }

  const copyTabletLink = async () => {
    if (!tabletUrl) return

    try {
      await navigator.clipboard.writeText(tabletUrl)
      setTabletLinkCopied(true)
      window.setTimeout(() => setTabletLinkCopied(false), 2200)
    } catch {
      setError(t("general:label_title_erro"), t("tableSelfService:copy_link_error"))
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              iconColor={THEME.colors.iconBGSecondaryColor}
              title={title}
              icon={faTable}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <>
                    {tableData && (
                      <div style={{ width: "100px", marginRight: "5px" }}>
                        <GenericDropDown
                          data={Object.mapEnumToStrings(EEnableStatusType, {
                            exclude: [EEnableStatusType.All],
                          })}
                          initialState={status}
                          disable={canEdite}
                          handlerFuncion={handleStatusChange}
                          descriptionKey="interactionDescriptions:pages.fluxo.tables.tablesPupop.select.select"
                        />

                        <GetSpace right={10} />
                      </div>
                    )}

                    <PopupHelpButton
                      helpKey="pages.fluxo.tables.tablespupop.1"
                      variant="global"
                      title={t("helpPopup:global.table.title")}
                      description={t("helpPopup:global.table.description")}
                      bullets={t("helpPopup:global.table.bullets", { returnObjects: true }) as string[]}
                    />

                    <GetSpace right={5} />

                    <ButtonIcon
                      icon={faClose}
                      size="small"
                      typeButton="button"
                      onClick={onClonse}
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      descriptionKey="interactionDescriptions:pages.fluxo.tables.tablesPupop.button.onClonse"
                    />
                  </>
                </div>
              }
            />

            <GetSpace top={10} />

            <Animate type={AnimationTypes.fadeInTraslate}>
              <form onSubmit={handleTrySubmit} className="p-1">
                <InputField
                  type="number"
                  value={numberTable}
                  icon={faTableCells}
                  maxLength={INPUT_SIZE.TABLE.number}
                  placeholder={t("label_table_name_place_holder")}
                  onInvalidMessage={t("label_table_name_onInvalidMessage")}
                  onChange={handleNumberTableChange}
                  disable={canEdite}
                  descriptionKey="interactionDescriptions:pages.fluxo.tables.tablesPupop.input.tableNamePlaceHolder"
                />

                <GetSpace top={8} />

                <InputField
                  type="text"
                  value={description}
                  icon={faClipboard}
                  maxLength={INPUT_SIZE.TABLE.description}
                  placeholder={t("label_table_description_place_holder")}
                  onInvalidMessage={t("label_table_description_onInvalidMessage")}
                  onChange={handleDescriptionChange}
                  disable={canEdite}
                  descriptionKey="interactionDescriptions:pages.fluxo.tables.tablesPupop.input.tableDescriptionPlaceHolder"
                />

                <div className={defaultStyle.divisionBar}></div>

                {tableData && companyMemory.isTableSelfServiceEnabled && (
                  <>
                    <section className="mb-4 overflow-hidden rounded-lg border-2 shadow-sm">
                      <Animate type={AnimationTypes.fadeInTraslate} duration="0.8s">
                        <div className="flex items-start gap-3 border-b border-orange-200 px-4 py-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-600 text-white shadow-md shadow-orange-200">
                            <Tablet size={22} />
                          </div>

                          <div className="min-w-0">
                            <p className="text-xs font-black uppercase tracking-widest text-orange-700">
                              {t("tableSelfService:title")}
                            </p>

                            <p className="mt-1 text-xs font-semibold text-slate-600">
                              {t("tableSelfService:management_description")}
                            </p>
                          </div>
                        </div>
                      </Animate>

                      <div className="p-4">
                        {isTabletLinked ? (
                          <Animate key="tablet-linked" type={AnimationTypes.slideUpIn} duration="0.7s">
                            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
                              <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm">
                                  <ShieldCheck size={21} />
                                </div>

                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-black uppercase tracking-widest text-emerald-800">
                                    {t("tableSelfService:linked")}
                                  </p>

                                  {tabletLastSeenAtUtc && (
                                    <p className="mt-3 flex items-center gap-2 text-[11px] font-bold text-emerald-800">
                                      <Clock3 size={14} />

                                      {t("tableSelfService:last_activity", {
                                        date: new Date(tabletLastSeenAtUtc).toLocaleString("pt-BR"),
                                      })}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <button
                                type="button"
                                disabled={canEdite || tabletAction !== null}
                                onClick={confirmRevokeTablet}
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-[11px] font-black uppercase tracking-widest text-white shadow-md transition-all hover:bg-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                                data-interaction-description-key="interactionDescriptions:pages.fluxo.tables.tablesPupop.button.confirmRevokeTablet"
                              >
                                {tabletAction === "revoke" ? (
                                  <LoaderCircle size={17} className="animate-spin" />
                                ) : (
                                  <Unlink size={17} />
                                )}
                                {tabletAction === "revoke"
                                  ? t("tableSelfService:unlinking")
                                  : t("tableSelfService:unlink")}
                              </button>
                            </div>
                          </Animate>
                        ) : (
                          <Animate key="tablet-available" type={AnimationTypes.slideUpIn} duration="0.7s">
                            <div className="p-1">
                              <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white shadow-sm">
                                  <Link2 size={20} />
                                </div>

                                <div>
                                  <p className="text-xs font-black uppercase tracking-widest text-sky-800">
                                    {hasPendingTabletActivation
                                      ? t("tableSelfService:waiting_activation")
                                      : t("tableSelfService:not_linked")}
                                  </p>

                                  <p className="mt-1 text-xs font-semibold text-sky-700">
                                    {hasPendingTabletActivation
                                      ? activationCode
                                        ? t("tableSelfService:waiting_activation_description")
                                        : t("tableSelfService:waiting_activation_hidden_code_description")
                                      : t("tableSelfService:not_linked_description")}
                                  </p>
                                </div>
                              </div>

                              {activationCode && (
                                <Animate type={AnimationTypes.zoomIn} duration="0.55s">
                                  <div className="mt-4 bg-white p-4 text-slate-700 shadow-sm">
                                    <p className="text-center text-3xl font-black tracking-[0.22em] text-slate-900">
                                      {activationCode}
                                    </p>

                                    <p className="mt-2 text-center text-[11px] font-bold text-slate-500">
                                      {t("tableSelfService:activation_expires")}
                                    </p>

                                    <p className="mt-3 break-all rounded-lg bg-slate-50 px-3 py-2 text-center text-[11px] font-semibold text-slate-600">
                                      {tabletUrl}
                                    </p>

                                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                      <button
                                        type="button"
                                        disabled={!tabletUrl}
                                        onClick={() => void copyTabletLink()}
                                        className="flex items-center justify-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-700 transition-all hover:border-orange-400 hover:text-orange-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                                        data-interaction-description-key="interactionDescriptions:pages.fluxo.tables.tablesPupop.button.voidCopyTabletLink"
                                      >
                                        {tabletLinkCopied ? <Check size={15} /> : <Copy size={15} />}
                                        {tabletLinkCopied
                                          ? t("tableSelfService:link_copied")
                                          : t("tableSelfService:copy_link")}
                                      </button>

                                      <button
                                        type="button"
                                        disabled={!tabletQrCodeUrl}
                                        onClick={() => setShowTabletQrCode((current) => !current)}
                                        className="flex items-center justify-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-700 transition-all hover:border-orange-400 hover:text-orange-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                                        data-interaction-description-key="interactionDescriptions:pages.fluxo.tables.tablesPupop.button.setShowTabletQrCodeCurrentCurrent"
                                      >
                                        <QrCode size={15} />
                                        {showTabletQrCode
                                          ? t("tableSelfService:hide_qr_code")
                                          : t("tableSelfService:show_qr_code")}
                                      </button>
                                    </div>

                                    {showTabletQrCode && (
                                      <Animate type={AnimationTypes.zoomIn} duration="0.45s">
                                        <div className="mt-4 bg-white p-3 text-center">
                                          <img
                                            src={tabletQrCodeUrl}
                                            alt={t("tableSelfService:qr_code_alt")}
                                            className="mx-auto aspect-square w-full max-w-56 rounded-lg"
                                          />

                                          <p className="mt-2 text-[10px] font-bold text-slate-500">
                                            {t("tableSelfService:qr_code_hint")}
                                          </p>
                                        </div>
                                      </Animate>
                                    )}
                                  </div>
                                </Animate>
                              )}

                              <button
                                type="button"
                                disabled={canEdite || tabletAction !== null}
                                onClick={() => void createTabletActivationCode()}
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-3 text-[11px] font-black uppercase tracking-widest text-white shadow-md transition-all hover:bg-orange-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                                data-interaction-description-key="interactionDescriptions:pages.fluxo.tables.tablesPupop.button.voidCreateTabletActivationCode"
                              >
                                {tabletAction === "generate" ? (
                                  <LoaderCircle size={17} className="animate-spin" />
                                ) : (
                                  <Link2 size={17} />
                                )}
                                {tabletAction === "generate"
                                  ? t("tableSelfService:generating_code")
                                  : hasPendingTabletActivation
                                    ? t("tableSelfService:generate_new_code")
                                    : t("tableSelfService:generate_code")}
                              </button>
                            </div>
                          </Animate>
                        )}
                      </div>
                    </section>

                    <div className={defaultStyle.divisionBar}></div>
                  </>
                )}

                <div className={defaultStyle.buttonsController}>
                  {tableData && (
                    <Button
                      label={t("button_fetch_relations")}
                      typeButton="button"
                      type="primary"
                      bgColor={THEME.colors.buttonBGSaveColor}
                      onClick={() => setShowTableOrdersPopup(true)}
                      descriptionKey="interactionDescriptions:pages.fluxo.tables.tablesPupop.button.fetchRelations"
                    />
                  )}

                  <CanView permission={Permissions.CanEditeTables}>
                    <Button
                      label={t("buttons:button_save")}
                      typeButton="submit"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGSaveColor}
                      descriptionKey="interactionDescriptions:pages.fluxo.tables.tablesPupop.button.save"
                    />
                  </CanView>

                  {tableData && (
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_register")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGRegisterColor}
                        onClick={handleEnableRegister}
                        descriptionKey="interactionDescriptions:pages.fluxo.tables.tablesPupop.button.register"
                      />
                    </>
                  )}

                  <CanView permission={Permissions.CanEditeTables}>
                    {tableData && (
                      <>
                        <GetSpace right={5} />

                        <Button
                          label={t("buttons:button_remove")}
                          typeButton="button"
                          type="primary"
                          size="medium"
                          iconColor={THEME.colors.buttonFGRemoveColor}
                          bgColor={THEME.colors.buttonBGRemoveColor}
                          onClick={() =>
                            setNewConfirmation(
                              t("general:label_title_remove"),
                              t("general:label_description_remove"),
                              async () => {
                                await handleRemove(tableData!, () => handleGetTable(onClonse))
                              },
                            )
                          }
                          descriptionKey="interactionDescriptions:pages.fluxo.tables.tablesPupop.button.remove"
                        />
                      </>
                    )}
                  </CanView>
                </div>
              </form>
            </Animate>
          </div>
        </Animate>

        {isRegisterEnable && tableData && (
          <ViewRegister id={tableData.id} typeEntity="TableEntityId" onClose={handleDisableRegister} />
        )}

        {showTableOrdersPopup && tableData && (
          <TableOrdersPopup tableData={tableData} onClose={() => setShowTableOrdersPopup(false)} />
        )}
      </div>
    </ModalPortal>
  )
}
